import { useSpring, animated, easings } from '@react-spring/web';
import classNames from 'classnames';

import FieldClose from '../icons/FieldCloseIcon';
import LoadingIcon from '../icons/LoadingIcon';
import SearchIcon from '../icons/SearchIcon';
import TextField, { TextFieldProps } from './TextField';

import styles from '../../styles/fields/search-field.module.css';

interface SearchFieldProps extends Omit<TextFieldProps, 'onChange'> {
    value?: string | null;
    loading?: boolean;
    onChange?: ((value: string | null) => void) | null;
    className?: string | null;
    inputClassName?: string | null;
}

function SearchField({
    value = null,
    loading = false,
    onChange = null,
    className = null,
    inputClassName = null,
    ...props
}: SearchFieldProps) {
    const onClickClear = () => {
        onChange(null);
    };

    const closeStyle = useSpring({
        config: {
            duration: 250,
            easing: easings.easeOutCubic,
        },
        delay: value !== null ? 100 : 0,
        to: { x: value !== null ? 0 : 20, opacity: value !== null && !loading ? 1 : 0 },
    });

    const loadingStyle = useSpring({
        config: {
            duration: 250,
            easing: easings.easeOutCubic,
        },
        delay: loading ? 100 : 0,
        to: { x: loading || value !== null ? 0 : 20, opacity: loading && value !== null ? 1 : 0 },
    });

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.hasValue]: value !== null,
                    [styles.isLoading]: loading,
                },
                className,
            ])}
        >
            <SearchIcon className={styles.searchIcon} />
            <TextField
                value={value}
                onChange={onChange}
                className={classNames([styles.input, inputClassName])}
                {...props}
            />
            <animated.button
                type="button"
                className={styles.closeButton}
                onClick={onClickClear}
                style={closeStyle}
            >
                <FieldClose className={styles.closeIcon} />
            </animated.button>
            <animated.div className={styles.loading} style={loadingStyle}>
                <LoadingIcon loading className={styles.loadingIcon} />
            </animated.div>
        </div>
    );
}

export default SearchField;
